import { Moon, Search, Sun, Menu, Activity } from "lucide-react";
import { NAV_ITEMS, type NavId } from "@/data/nav";
import { usePhases } from "@/hooks/useLive";

interface Props {
  active: NavId;
  onOpenCommand: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onOpenMobileSidebar: () => void;
}

export function Header({ active, onOpenCommand, theme, onToggleTheme, onOpenMobileSidebar }: Props) {
  const current = NAV_ITEMS.find((n) => n.id === active);
  const PHASES = usePhases();
  const activePhase = PHASES.find((p) => p.status === "active");

  return (
    <header className="sticky top-0 z-20 flex items-center gap-4 border-b border-border bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/75 md:px-7">
      {/* Hamburger — mobile only */}
      <button
        type="button"
        onClick={onOpenMobileSidebar}
        className="rounded-md border border-border bg-card p-2 text-muted-foreground hover:text-foreground md:hidden"
        aria-label="Open menu"
      >
        <Menu size={16} />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="truncate text-h2 text-foreground">{current?.label ?? "Overview"}</h1>
          {activePhase && (
            <span className="inline-flex items-center gap-1 text-caption text-muted-foreground">
              <Activity size={11} className="text-sky-500" />
              <span className="font-medium text-sky-700">{activePhase.name}</span>
              {activePhase.date && <span className="tabular">· {activePhase.date}</span>}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onOpenCommand}
          aria-label="Open command palette"
          className="hidden items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-caption text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground md:flex"
        >
          <Search size={14} />
          <span>Search…</span>
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-micro">⌘K</kbd>
        </button>

        {/* Mobile search icon only */}
        <button
          type="button"
          onClick={onOpenCommand}
          aria-label="Search"
          className="rounded-md border border-border bg-card p-2 text-muted-foreground hover:text-foreground md:hidden"
        >
          <Search size={16} />
        </button>

        <button
          type="button"
          onClick={onToggleTheme}
          aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          className="rounded-md border border-border bg-card p-2 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      </div>
    </header>
  );
}
